import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import GameCard from "../components/games/GameCard";
import Button from "../components/ui/Button";
import { getGamesByTag } from "../api/games";
import { useAuth } from "../AuthContext";

const PAGE_SIZE = 20;

const GameCardSkeleton = () => (
  <div className="rounded-3xl border border-white/10 bg-surface-card/60 backdrop-blur-md shadow-card overflow-hidden">
    <div className="h-[200px] gg-shimmer" />
    <div className="p-4 space-y-2.5">
      <div className="h-6 rounded gg-shimmer" />
      <div className="h-4 w-2/3 rounded gg-shimmer" />
      <div className="flex gap-2 mt-2">
        <div className="h-6 w-14 rounded-full gg-shimmer" />
        <div className="h-6 w-20 rounded-full gg-shimmer" />
      </div>
    </div>
  </div>
);

const gridClass =
  "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5 w-full max-w-[1400px] mx-auto";

export default function TagPage() {
  const { tag: rawTag } = useParams();
  const tag = decodeURIComponent(rawTag || "");
  const navigate = useNavigate();
  const { blacklist } = useAuth();

  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true); // initial / tag-change load
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(null);

  // Load the first page whenever the tag changes (clicking another tag on a
  // card while already on this page). Shows the full skeleton state.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setGames([]);
    setPage(1);
    window.scrollTo({ top: 0, behavior: "smooth" });

    getGamesByTag(tag, 1, PAGE_SIZE, blacklist || [])
      .then((res) => {
        if (cancelled) return;
        setGames(res.games || []);
        setTotalPages(res.pagination?.totalPages || 1);
        setTotal(res.pagination?.total || 0);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Something went wrong loading games for this tag.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [tag, blacklist]);

  const handleLoadMore = useCallback(async () => {
    if (loadingMore || page >= totalPages) return;
    const nextPage = page + 1;
    setLoadingMore(true);
    try {
      const res = await getGamesByTag(tag, nextPage, PAGE_SIZE, blacklist || []);
      setGames((prev) => {
        const existing = new Set(prev.map((g) => g.id));
        const fresh = (res.games || []).filter((g) => g && !existing.has(g.id));
        return [...prev, ...fresh];
      });
      setPage(nextPage);
      setTotalPages(res.pagination?.totalPages || totalPages);
    } catch {
      setError("Failed to load more games.");
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, page, totalPages, tag, blacklist]);

  return (
    <div className="min-h-[calc(100vh-var(--header-height))] overflow-x-clip py-6 sm:py-8 md:py-10 px-3 sm:px-6 md:px-8">
      <div className="max-w-[1400px] mx-auto">
        {/* Hero */}
        <div className="relative overflow-hidden rounded-[28px] border border-white/10 bg-surface-card/40 backdrop-blur-md mb-7 sm:mb-8 px-6 sm:px-10 py-9 sm:py-12">
          <div className="absolute inset-0 gg-grid-backdrop opacity-40" />
          <div className="absolute -top-24 left-1/2 -translate-x-1/2 h-56 w-[70%] rounded-full bg-brand-purple/25 blur-3xl animate-pulseGlow" />
          <div className="absolute top-6 right-8 h-24 w-24 rounded-full bg-brand-gold/15 blur-2xl animate-floaty" />
          <div className="relative text-center">
            <button
              onClick={() => navigate("/games")}
              className="group mb-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-4 py-1.5 text-xs font-semibold text-white/70 transition-all duration-200 hover:border-brand-gold/50 hover:text-brand-gold"
            >
              <span className="transition-transform duration-200 group-hover:-translate-x-0.5">←</span>
              Browse all games
            </button>
            <p className="text-[0.7rem] font-bold uppercase tracking-[0.25em] text-brand-gold/80 mb-1">
              Tag
            </p>
            <h1 className="font-heading font-extrabold text-white text-4xl sm:text-5xl md:text-6xl tracking-tight break-words">
              <span className="gg-gradient-text">#{tag}</span>
            </h1>
            <p className="text-white/65 text-sm sm:text-base mt-3">
              {loading
                ? "Finding games…"
                : total > 0
                  ? `${total} game${total > 1 ? "s" : ""} tagged “${tag}”.`
                  : `No games found for “${tag}”.`}
            </p>
          </div>
        </div>

        {error && (
          <div className="mb-5 max-w-[1400px] mx-auto rounded-xl px-4 py-3 border border-red-400/40 bg-red-500/10 text-white text-sm flex items-center justify-between gap-3">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="text-white/60 hover:text-white" aria-label="Dismiss">
              ✕
            </button>
          </div>
        )}

        {loading ? (
          <div className={gridClass}>
            {[...Array(10)].map((_, i) => (
              <GameCardSkeleton key={i} />
            ))}
          </div>
        ) : games.length > 0 ? (
          <>
            <div className={`${gridClass} animate-fadeIn`}>
              {games.map((game, idx) => (
                <div
                  key={game.id || idx}
                  style={{ animationDelay: `${Math.min(idx, 10) * 35}ms` }}
                  className="animate-fadeUp"
                >
                  <GameCard game={game} />
                </div>
              ))}
            </div>

            {/* Skeleton row while loading more */}
            {loadingMore && (
              <div className={`${gridClass} mt-5`}>
                {[...Array(5)].map((_, i) => (
                  <GameCardSkeleton key={`more-${i}`} />
                ))}
              </div>
            )}

            <div className="flex justify-center mt-8 sm:mt-10">
              {page < totalPages ? (
                <Button variant="primary" size="lg" onClick={handleLoadMore} disabled={loadingMore}>
                  {loadingMore ? "Loading…" : "Load More Games"}
                </Button>
              ) : (
                <p className="text-white/45 text-sm text-center">
                  ✨ You've seen all {total} game{total > 1 ? "s" : ""} tagged “{tag}”
                </p>
              )}
            </div>
          </>
        ) : (
          <div className="max-w-xl mx-auto rounded-3xl border border-white/10 bg-surface-card/40 backdrop-blur-md px-6 py-14 text-center animate-fadeUp">
            <div className="text-5xl mb-4">🔍</div>
            <h2 className="font-heading font-bold text-2xl text-white mb-2">
              No games for “{tag}”
            </h2>
            <p className="text-white/55 mb-6 max-w-sm mx-auto">
              We couldn't find any games with this tag. Try browsing all games instead.
            </p>
            <Button variant="primary" size="lg" onClick={() => navigate("/games")}>
              Browse games
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
